import React, { useState, useEffect } from 'react';
import { Search, Mail, Phone, Calendar, ClipboardList, MoreHorizontal, Send } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { db } from '../../../lib/db';
import { formatDate, getYearsOfService } from '../../../lib/dateUtils';
import { formatStatus, getStatusBadgeColor } from '../../../lib/calculations';
import { useAuthStore } from '../../../store/authStore';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { toast } from 'sonner';

export default function TeamPage() {
  const { user } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, [user]);

  const loadData = async () => {
    try {
      const [usersData, appraisalsData] = await Promise.all([
        db.users.toArray(),
        db.appraisals.toArray()
      ]);
      const team = usersData.filter(u => u.supervisorId === user?.id && u.status === 'active');
      setTeamMembers(team);
      setAppraisals(appraisalsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch = searchQuery === '' ||
      member.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const getMemberAppraisal = (memberId) => appraisals.find(a => a.userId === memberId);

  const sendReminder = (member) => {
    toast.success(`Reminder sent to ${member.profile?.firstName}`);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><span className="text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Team</h1>
          <p className="text-gray-500 mt-1">Manage your direct reports</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-violet-600">{teamMembers.length}</p>
          <p className="text-sm text-gray-500">Team Members</p>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search team members..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredMembers.map(member => {
          const appraisal = getMemberAppraisal(member.id);
          return (
            <Card key={member.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                      {member.profile?.firstName?.[0]}{member.profile?.lastName?.[0]}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{member.profile?.firstName} {member.profile?.lastName}</h3>
                      <p className="text-sm text-gray-500">{member.profile?.position}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem><ClipboardList className="w-4 h-4 mr-2" /> View Appraisal</DropdownMenuItem>
                      <DropdownMenuItem><Calendar className="w-4 h-4 mr-2" /> Schedule Meeting</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => sendReminder(member)}><Send className="w-4 h-4 mr-2" /> Send Reminder</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600"><Mail className="w-4 h-4 text-gray-400" />{member.email}</div>
                  <div className="flex items-center gap-2 text-sm text-gray-600"><Phone className="w-4 h-4 text-gray-400" />{member.profile?.phone || 'N/A'}</div>
                </div>
                {appraisal ? (
                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-500">Current Appraisal</span>
                      <Badge className={getStatusBadgeColor(appraisal.status)}>{formatStatus(appraisal.status)}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Progress value={appraisal.progress?.overallPercent || 0} className="flex-1 h-2" />
                      <span className="text-sm text-gray-500">{appraisal.progress?.overallPercent || 0}%</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Due: {formatDate(appraisal.dueDate)}</p>
                  </div>
                ) : (
                  <div className="pt-4 border-t border-gray-100"><p className="text-sm text-gray-500">No active appraisal</p></div>
                )}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <Badge variant="secondary">{member.profile?.department}</Badge>
                  <span className="text-xs text-gray-400">{getYearsOfService(member.profile?.hireDate)}y tenure</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredMembers.length === 0 && <div className="text-center py-12"><p className="text-gray-500">No team members found</p></div>}
    </div>
  );
}
